<script>
    import { toast } from "@zerodevx/svelte-toast";
    import Swal from "sweetalert2";
    import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
    import * as m from "@app/paraglide/messages.js";
    import {
        knpState,
        saveKnpGuide,
        deleteKnpGuide,
    } from "@app/store/knpStore.svelte.js";
    import supportedLanguages from "@cclib/dict/supportedLanguages.js";

    let { manageGuidePermissions, isAdminOrManager } = $props();

    let editingGuideForm = $state(false);
    let newLanguageCode = $state("");
    
    // Form state for guide editing
    let guideForm = $state({
        title: "",
        description: "",
        original_language: "en-US",
        languages: [],
        translation_notes: ""
    });

    // Update form when guide is selected
    $effect(() => {
        if (knpState.selectedGuide) {
            guideForm.title = knpState.selectedGuide.title;
            guideForm.description = knpState.selectedGuide.description || "";
            guideForm.original_language = knpState.selectedGuide.original_language || "en-US";
            guideForm.languages = [...(knpState.selectedGuide.languages || ["en-US"])];
            guideForm.translation_notes = knpState.selectedGuide.translation_notes || "";
            editingGuideForm = false;
        }
    });

    async function handleDeleteGuide() {
        if (!knpState.selectedGuide) return;

        const result = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.destructive,
            title: m.knp_guide_delete_title(),
            text: m.knp_guide_delete_text({ title: knpState.selectedGuide.title }),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: m.delete(),
            cancelButtonText: m.cancel(),
        });

        if (result.isConfirmed) {
            const guideId = knpState.selectedGuide.id;
            deleteKnpGuide(guideId)
                .then(() => {
                    toast.push(m.knp_guide_deleted_success(), {
                        classes: ["toast-success"],
                    });
                })
                .catch((error) => {
                    console.error("Error deleting guide:", error);
                    toast.push(m.knp_guide_delete_failed({ error: error.message }), {
                        classes: ["toast-danger"],
                    });
                });
        }
    }

    function handleSaveGuide() {
        if (!knpState.selectedGuide) return;

        const updatedGuide = {
            ...knpState.selectedGuide,
            title: guideForm.title,
            description: guideForm.description,
            original_language: guideForm.original_language,
            languages: guideForm.languages,
            translation_notes: guideForm.translation_notes
        };

        saveKnpGuide(updatedGuide)
            .then(() => {
                toast.push(m.knp_guide_saved(), {
                    classes: ["toast-success"],
                });
                editingGuideForm = false;
            })
            .catch((error) => {
                console.error("Error saving guide:", error);
                toast.push(m.knp_guide_save_failed({ error: error.message }), {
                    classes: ["toast-danger"],
                });
            });
    }

    function addLanguage() {
        if (!newLanguageCode.trim()) return;
        
        if (!guideForm.languages.includes(newLanguageCode)) {
            guideForm.languages = [...guideForm.languages, newLanguageCode];
            newLanguageCode = "";
            editingGuideForm = true;
        }
    }

    function removeLanguage(lang) {
        guideForm.languages = guideForm.languages.filter((l) => l !== lang);
    }
</script>

<div class="tab-pane active">
    <form>
        <div class="mb-3">
            <label class="form-label" for="guideTitle">{m.knp_guide_title_label()}</label>
            <input
                type="text"
                class="form-control"
                id="guideTitle"
                bind:value={guideForm.title}
                oninput={() => editingGuideForm = true}
            />
        </div>

        <div class="mb-3">
            <label class="form-label" for="guideDescription">{m.knp_guide_description_label()}</label>
            <textarea
                class="form-control"
                id="guideDescription"
                rows="3"
                bind:value={guideForm.description}
                oninput={() => editingGuideForm = true}
            ></textarea>
        </div>

        <div class="mb-3">
            <label class="form-label" for="guideTranslationNotes">{m.knp_guide_translation_notes_label()}</label>
            <textarea
                class="form-control"
                id="guideTranslationNotes"
                rows="3"
                maxlength="2000"
                bind:value={guideForm.translation_notes}
                oninput={() => editingGuideForm = true}
            ></textarea>
            <small class="form-text text-muted">{m.knp_guide_translation_notes_help()}</small>
        </div>

        <div class="mb-3">
            <label class="form-label" for="originalLanguage">{m.knp_guide_original_language_label()}</label>
            <select
                class="form-select"
                id="originalLanguage"
                bind:value={guideForm.original_language}
                onchange={() => editingGuideForm = true}
            >
                {#each supportedLanguages.languages as langCode (langCode)}
                    <option value={langCode}>{langCode}</option>
                {/each}
            </select>
            <small class="form-text text-muted">{m.knp_guide_original_language_help()}</small>
        </div>

        <div class="mb-3">
            <div class="form-label fw-bold">{m.knp_guide_languages_label()}</div>
            <div class="d-flex gap-2 mb-2">
                <select
                    class="form-select"
                    bind:value={newLanguageCode}
                >
                    <option value="">{m.knp_guide_language_select_placeholder()}</option>
                    {#each supportedLanguages.languages as langCode (langCode)}
                        {#if !guideForm.languages.includes(langCode)}
                            <option value={langCode}>{langCode}</option>
                        {/if}
                    {/each}
                </select>
                <button
                    type="button"
                    class="btn btn-secondary"
                    onclick={addLanguage}
                    disabled={!newLanguageCode}
                >
                    {m.add()}
                </button>
            </div>
            <div class="d-flex flex-wrap gap-2">
                {#each guideForm.languages as lang (lang)}
                    <span class="badge bg-secondary">
                        {lang}
                        <button
                            type="button"
                            class="btn-close btn-close-white ms-1"
                            style="font-size: 0.6rem;"
                            onclick={() => (removeLanguage(lang), editingGuideForm = true)}
                            aria-label={m.knp_guide_remove_language_aria()}
                        ></button>
                    </span>
                {/each}
            </div>
        </div>

        <!-- Permissions Display -->
        <div class="mb-3">
            <div class="form-label fw-bold">{m.knp_guide_access_permissions_label()}</div>
            {#if !knpState.selectedGuide.groups || knpState.selectedGuide.groups.length === 0}
                <div class="alert alert-info py-2 mb-2">
                    <i class="bi bi-globe me-2"></i>
                    <strong>{m.knp_guide_all_team_members()}</strong> - {m.knp_guide_all_team_members_text()}
                </div>
            {:else}
                <div class="alert alert-warning py-2 mb-2">
                    <i class="bi bi-lock-fill me-2"></i>
                    <strong>{m.knp_guide_restricted_access()}</strong> - {m.knp_guide_restricted_access_text({ count: knpState.selectedGuide.groups.length })}
                </div>
            {/if}
            
            {#if isAdminOrManager}
                <button
                    type="button"
                    class="btn btn-outline-primary btn-sm"
                    onclick={manageGuidePermissions}
                >
                    <i class="bi bi-shield-lock me-1"></i>
                    {m.knp_guide_manage_permissions()}
                </button>
                <small class="form-text text-muted d-block mt-2">
                    {m.knp_guide_admin_manager_full_access()}
                </small>
            {:else}
                <small class="form-text text-muted">
                    {m.knp_guide_contact_admin_permissions()}
                </small>
            {/if}
        </div>

        <div class="d-flex gap-2">
            <button
                type="button"
                class="btn btn-primary"
                onclick={handleSaveGuide}
                disabled={!editingGuideForm}
            >
                {m.save_changes()}
            </button>
            <button
                type="button"
                class="btn btn-danger"
                onclick={handleDeleteGuide}
            >
                {m.knp_guide_delete_guide()}
            </button>
        </div>
    </form>

    <hr class="my-4" />

    <div class="small text-muted">
        <p><strong>{m.knp_guide_id_label()}</strong> {knpState.selectedGuide.id}</p>
        <p><strong>{m.knp_guide_created_by_label()}</strong> {knpState.selectedGuide.createdBy || m.knp_guide_unknown()}</p>
        <p><strong>{m.knp_guide_created_label()}</strong> {knpState.selectedGuide.createdOn?.toDate?.()?.toLocaleString() || m.knp_guide_not_available()}</p>
        <p><strong>{m.knp_guide_updated_label()}</strong> {knpState.selectedGuide.updatedOn?.toDate?.()?.toLocaleString() || m.knp_guide_not_available()}</p>
    </div>
</div>
